import PageWrapper from "../../components/page-wrapper/page-wrapper.components";
import { useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { selectCurrentUser } from "../../redux/user/user.selectors";
import { sessionExpired, updateToken } from "../../redux/user/user.actions";
import { authFetch, getApiUrl, SessionExpiredError } from "../../utils/api";
import { Calculator, Plus, ChevronDown, ChevronUp, Eye, Trash2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X, AlertTriangle } from "lucide-react";
import QuoteTool from "../../components/quotes/quote-tool.component";
import PDFGenerator from "../../components/quotes/quote-pdf.component";
import { usePagination, useSortBy, useTable } from "react-table";
import QuoteEditTool from "../../components/quotes/quote-edit-tool.component";

const QuotesDashboard = ({ currentUser, onSessionExpired, onTokenRefresh }) => {
    const apiUrl = getApiUrl();
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };

    const [proposalsList, setProposalsList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showQuoteTool, setShowQuoteTool] = useState(false);
    const [selectedProposal, setSelectedProposal] = useState(null);
    const [showQuoteEditTool, setShowQuoteEditTool] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [proposalToDelete, setProposalToDelete] = useState(null);
    const [customPer, setCustomPercentage] = useState({});

    const handleUpdatePercentage = (accountType, value) => {
        setCustomPercentage((prev) => ({
            ...prev,
            [accountType]: parseFloat(value) || 0,
        }));
    }

    useEffect(() => {
        getAllProposals();
    }, []);

    const handleDeleteClick = (id) => {
        setProposalToDelete(id);
        setShowDeleteModal(true);
    }

    const handleConfirmDelete = async () => {
        try {
            const response = await authFetch(
                `${apiUrl}/proposals/${proposalToDelete.id}`,
                {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                },
                authParams
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            await getAllProposals();
        } catch (error) {
            if (!(error instanceof SessionExpiredError)) {
                console.error('Error:', error);
            }
        }

        setShowDeleteModal(false);
    }

    const getAllProposals = async () => {
        try {
            const response = await authFetch(
                `${apiUrl}/proposals/${currentUser.id}`,
                { method: 'GET' },
                authParams
            );

            if (response.ok) {
                const data = await response.json();
                setProposalsList(data.proposalsList);
            }
            else {
                console.error("HTTP Error:", response.status, response.statusText);
                return;
            }
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) {
                toast.error("Failed to retrieve proposals");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const columns = useMemo(() => {
        return [
            {
                Header: 'ID',
                accessor: (row, index) => index + 1,
            },
            {
                Header: 'Client Company',
                accessor: 'clientCompany',
            },
            {
                Header: 'Client Name',
                accessor: 'clientName',
            },
            {
                Header: 'Created At',
                accessor: (row) => row.createdAt ? new Date(row.createdAt).toISOString().split('T')[0] : '',
            },
            {
                Header: 'Actions',
                Cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <PDFGenerator proposal={row.original}
                            customPercentage={customPer}
                            currentUser={currentUser}
                            onSessionExpired={onSessionExpired}
                            onTokenRefresh={onTokenRefresh} />
                        <button
                            className="p-1.5 text-primary border border-primary rounded-lg hover:bg-primary hover:!text-white transition-colors"
                            onClick={() => handleViewButtonClick(row.original)}
                            title="View/Edit"
                        >
                            <Eye className="w-4 h-4" />
                        </button>
                        <button
                            className="p-1.5 text-red-500 border border-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-colors"
                            onClick={() => handleDeleteClick(row.original)}
                            title="Delete"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ),
            },
        ];
    }, [currentUser, customPer]);

    const data = useMemo(() => proposalsList, [proposalsList]);

    const handleViewButtonClick = (proposal) => {
        setShowQuoteEditTool(true);
        setSelectedProposal(proposal);
    }

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: {
                pageIndex: 0,
            },
        },
        useSortBy,
        usePagination
    );

    return (
        <PageWrapper>
            <div className="flex-1 px-6 md:px-12 lg:pl-12 lg:pr-24 py-6 pb-5 overflow-y-auto">
                {/* page header with icon */}
                <div className="flex items-center justify-between gap-3 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-xl">
                            <Calculator className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                                Quotes
                            </h1>
                            <p className="text-sm text-gray-500">
                                Quotes you’ve created{!isLoading && ` (${proposalsList.length} total)`}
                            </p>
                        </div>
                    </div>
                    <button
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary-dark active:scale-[0.98] transition-all"
                        onClick={() => setShowQuoteTool(true)}
                    >
                        <Plus className="w-4 h-4" />
                        <span className="hidden sm:inline">Create</span>
                    </button>
                </div>

                {/* content */}
                <div>
                    {isLoading ? null : data && data.length > 0 ? (
                        <div className="bg-white rounded-2xl shadow-card p-4 sm:p-6">
                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table {...getTableProps()} className="w-full text-sm">
                                    <thead>
                                        {headerGroups.map(headerGroup => (
                                            <tr {...headerGroup.getHeaderGroupProps()} className="border-b border-gray-100">
                                                {headerGroup.headers.map(column => (
                                                    <th
                                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                                        className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 transition-colors"
                                                    >
                                                        <span className="inline-flex items-center gap-1">
                                                            {column.render('Header')}
                                                            {column.isSorted && (
                                                                column.isSortedDesc
                                                                    ? <ChevronDown className="w-3 h-3" />
                                                                    : <ChevronUp className="w-3 h-3" />
                                                            )}
                                                        </span>
                                                    </th>
                                                ))}
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody {...getTableBodyProps()} className="divide-y divide-gray-50">
                                        {page.map(row => {
                                            prepareRow(row);
                                            return (
                                                <tr {...row.getRowProps()} key={row.id} className="hover:bg-gray-50 transition-colors">
                                                    {row.cells.map(cell => (
                                                        <td {...cell.getCellProps()} className="py-3 px-2 text-gray-700">
                                                            {cell.render('Cell')}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="md:hidden space-y-3">
                                {page.map((row, idx) => {
                                    prepareRow(row);
                                    const proposal = row.original;
                                    const createdAt = proposal.createdAt ? new Date(proposal.createdAt).toISOString().split('T')[0] : '';
                                    return (
                                        <div key={proposal.id || idx} className="bg-gray-50 rounded-xl p-4">
                                            <div className="flex justify-between items-start gap-3 mb-2">
                                                <div className="min-w-0 flex-1">
                                                    <h4 className="font-semibold text-gray-900 truncate">{proposal.clientCompany}</h4>
                                                    <p className="text-sm text-gray-600">{proposal.clientName}</p>
                                                </div>
                                                <span className="text-xs text-gray-400 flex-shrink-0">#{idx + 1}</span>
                                            </div>
                                            <div className="text-sm text-gray-500 space-y-1 mb-3">
                                                <p className="truncate">{proposal.clientEmail}</p>
                                                <p className="text-xs">Created: {createdAt}</p>
                                            </div>
                                            <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                                                <PDFGenerator
                                                    proposal={proposal}
                                                    customPercentage={customPer}
                                                    currentUser={currentUser}
                                                    onSessionExpired={onSessionExpired}
                                                    onTokenRefresh={onTokenRefresh}
                                                />
                                                <button
                                                    className="p-2 text-primary border border-primary rounded-lg hover:bg-primary hover:!text-white transition-colors"
                                                    onClick={() => handleViewButtonClick(proposal)}
                                                    title="View/Edit"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    className="p-2 text-red-500 border border-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-colors"
                                                    onClick={() => handleDeleteClick(proposal)}
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* pagination */}
                            <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-100">
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => gotoPage(0)}
                                        disabled={!canPreviousPage}
                                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="First page"
                                    >
                                        <ChevronsLeft className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={previousPage}
                                        disabled={!canPreviousPage}
                                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Previous page"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={nextPage}
                                        disabled={!canNextPage}
                                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Next page"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => gotoPage(pageOptions.length - 1)}
                                        disabled={!canNextPage}
                                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Last page"
                                    >
                                        <ChevronsRight className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                    <span>
                                        Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
                                    </span>
                                    <span className="flex items-center gap-2">
                                        Go to:
                                        <input
                                            type="number"
                                            min={1}
                                            max={pageOptions.length}
                                            defaultValue={pageIndex + 1}
                                            onChange={(e) => {
                                                const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                                                gotoPage(pageNumber);
                                            }}
                                            className="w-14 px-2 py-1 border border-gray-200 rounded-lg text-center focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                        />
                                    </span>
                                </div>

                                <select
                                    value={pageSize}
                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                >
                                    {[10, 20, 30, 40].map((size) => (
                                        <option key={size} value={size}>
                                            Show {size}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow-card p-6">
                            <p className="text-center text-gray-500">
                                No proposals available. Click "Create" to generate a new one.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <QuoteTool currentUser={currentUser}
                showQuoteTool={showQuoteTool} setShowQuoteTool={setShowQuoteTool}
                customPercentage={customPer} handleUpdatePercentage={handleUpdatePercentage} />
            <QuoteEditTool currentUser={currentUser} proposal={selectedProposal} setProposal={setSelectedProposal}
                showQuoteEditTool={showQuoteEditTool}
                setShowQuoteEditTool={setShowQuoteEditTool}
                customPercentage={customPer} handleUpdatePercentage={handleUpdatePercentage} />

            {/* delete confirmation modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-modal w-full max-w-sm p-5">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="p-2 bg-red-50 rounded-lg flex-shrink-0">
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900">Delete Quote</h3>
                                <p className="text-sm text-gray-500 mt-1">This action cannot be undone.</p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PageWrapper>
    )
}


const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
    onSessionExpired: () => dispatch(sessionExpired()),
    onTokenRefresh: (token) => dispatch(updateToken(token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(QuotesDashboard);
